import React, { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Award } from 'lucide-react';
import { useCVData, type CVCertification } from '../../../lib/cv-mock-data';
import { WebInput, WebSaveButton, WebAddButton, WebFormDialog, WebItemCard, WebEmptyState } from './web-form-elements';
import { WebDatePicker } from './web-pickers';
import { validateCertification, type CertificationErrors } from './cv-form-validation';
import { useHydratedArrayState } from './useHydratedFormState';
import { confirmDeleteCvItem } from './delete-confirmation';
import { cvChangeNotApplied } from '../../../lib/cv-write-coordinator';

interface EditCertificationsFormProps { onSave?: () => Promise<void> | void; closeOnSave?: boolean; ctaLabel?: string; }
function newDraftId(): string { return `local-${Math.random().toString(36).slice(2, 11)}`; }
function emptyItem(): CVCertification { return { id: newDraftId(), name: '', issuer: '', date: '', credentialUrl: '' }; }
function cloneCertification(item: CVCertification): CVCertification { return { ...item }; }

const ERROR_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--tabbio-danger, #e53e3e)',
  marginTop: 4,
  display: 'block',
};

export function EditCertificationsForm({ onSave, closeOnSave = true, ctaLabel }: EditCertificationsFormProps) {
  const { t } = useTranslation('seeker');
  const { profile, updateProfile } = useCVData();
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  const [items, setItems] = useHydratedArrayState(profile.certifications, cloneCertification);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<CVCertification>(emptyItem());
  const [showErrors, setShowErrors] = useState(false);
  const errors = validateCertification(draft, t);
  const hasErrors = Object.keys(errors).length > 0;
  const visibleErrors: CertificationErrors = showErrors ? errors : {};

  const openEdit = (i: number) => { setDraft({ ...items[i] }); setEditingIndex(i); setShowErrors(false); };
  const openAdd = () => { setDraft(emptyItem()); setEditingIndex(-1); setShowErrors(false); };

  const handleDialogSave = () => {
    if (hasErrors) { setShowErrors(true); return; }
    if (editingIndex === -1) setItems([...items, draft]);
    else if (editingIndex !== null) { const next = [...items]; next[editingIndex] = draft; setItems(next); }
    setEditingIndex(null);
    setShowErrors(false);
  };

  const handleCardDelete = (i: number) => {
    void confirmDeleteCvItem(t, 'certification', () => setItems(items.filter((_, idx) => idx !== i)));
  };

  const handleSave = async () => {
    if (isSaving) return;
    updateProfile({ certifications: items.filter((e) => e.name.trim()) });
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
        toast.success(t('cv.edit.saved'));
        if (closeOnSave && router.canGoBack()) router.back();
      } catch (error) {
        // B02: a CV conflict the user answered is not a failure. "Discard"
        // keeps the version saved elsewhere; "keep editing" keeps this draft.
        const outcome = cvChangeNotApplied(error);
        if (outcome) {
          if (outcome === 'discarded' && closeOnSave && router.canGoBack()) router.back();
          return;
        }
        toast.error(t('cv.edit.saveFailed'));
      } finally {
        setIsSaving(false);
      }
    } else {
      router.back();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((cert, i) => (
        <WebItemCard key={cert.id} title={cert.name} subtitle={[cert.issuer, cert.date].filter(Boolean).join(' · ')} icon={<Award size={18} color="var(--text-secondary)" strokeWidth={1.75} />} onClick={() => openEdit(i)} onDelete={() => handleCardDelete(i)} />
      ))}
      {items.length === 0 && <WebEmptyState icon={<Award size={32} color="var(--text-tertiary)" strokeWidth={1.25} />} text="No certifications yet" />}
      <WebAddButton label={t('cv.edit.addCertification')} onClick={openAdd} />
      <WebSaveButton label={ctaLabel ?? t('cv.edit.save')} onClick={handleSave} loading={isSaving} disabled={isSaving} />

      <WebFormDialog visible={editingIndex !== null} title={editingIndex === -1 ? t('cv.edit.addCertification') : t('cv.edit.certifications')} onClose={() => setEditingIndex(null)}>
        <WebInput label={t('cv.edit.certName')} value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder={t('cv.edit.certName')} error={visibleErrors.name} />
        <WebInput label={t('cv.edit.issuer')} value={draft.issuer} onChange={(v) => setDraft({ ...draft, issuer: v })} placeholder={t('cv.edit.issuer')} error={visibleErrors.issuer} />
        <div style={{ marginTop: 12 }}>
          <WebDatePicker label={t('cv.edit.date')} value={draft.date} onSelect={(v) => setDraft({ ...draft, date: v })} placeholder={t('cv.edit.date')} />
          {visibleErrors.date ? <span style={ERROR_STYLE}>{visibleErrors.date}</span> : null}
        </div>
        <WebInput label={t('cv.edit.credentialUrl')} value={draft.credentialUrl ?? ''} onChange={(v) => setDraft({ ...draft, credentialUrl: v })} placeholder={t('cv.edit.credentialUrlHint')} error={visibleErrors.credentialUrl} />
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            onClick={handleDialogSave}
            disabled={showErrors && hasErrors}
            style={{
              flex: 1,
              height: 48,
              borderRadius: 9999,
              backgroundColor: 'var(--text-primary)',
              color: 'var(--bg)',
              fontSize: 16,
              fontWeight: 600,
              border: 'none',
              cursor: showErrors && hasErrors ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: showErrors && hasErrors ? 0.5 : 1,
            }}
          >
            {editingIndex === -1 ? t('cv.edit.addCertification') : t('cv.edit.save')}
          </button>
        </div>
      </WebFormDialog>
    </div>
  );
}
